import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import {
  Sun,
  Moon,
  MagnifyingGlass,
  Bell,
  SignOut,
  CaretDown,
  User,
  Gear
} from "@phosphor-icons/react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

function initials(name = "") {
  return name
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

const ROLE_LABEL = {
  super_admin: "Super Admin",
  sales_manager: "Sales Manager",
  finance: "Finance",
  employee: "Employee",
  investor_view: "Investor",
  production_manager: "Production",
  read_only: "Read-only",
};

export default function Topbar() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const [q, setQ] = useState("");

  return (
    <header
      className="h-16 border-b border-border glass sticky top-0 z-30 flex items-center px-4 lg:px-8"
      data-testid="topbar"
    >
      <div className="flex-1 flex items-center gap-3 max-w-2xl">
        <div className="relative w-full">
          <MagnifyingGlass
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            data-testid="topbar-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search customers, leads, tasks... ?K"
            className="pl-9 h-10 bg-background/40 border-border/80 focus-visible:ring-1 focus-visible:ring-primary"
          />
        </div>
      </div>

      <div className="flex items-center gap-1.5 ml-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={toggle}
          data-testid="theme-toggle"
          className="h-9 w-9 rounded-full"
          aria-label="Toggle theme"
        >
          {theme === "dark" ? (
            <Sun size={16} weight="duotone" />
          ) : (
            <Moon size={16} weight="duotone" />
          )}
        </Button>

        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 rounded-full relative"
          data-testid="notifications-btn"
        >
          <Bell size={16} weight="duotone" />
          <span className="absolute top-2 right-2 w-1.5 h-1.5 rounded-full bg-primary"></span>
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="user-menu-trigger"
              className="ml-2 flex items-center gap-2.5 pl-1 pr-2.5 py-1 rounded-full hover:bg-muted/60 transition-colors"
            >
              <div className="w-8 h-8 rounded-full bg-primary/15 hairline-border flex items-center justify-center text-primary text-xs font-medium">
                {initials(user?.name)}
              </div>

              <div className="hidden md:flex flex-col items-start leading-none">
                <span className="text-sm">{user?.name}</span>
                <span className="overline mt-1">
                  {ROLE_LABEL[user?.role] || user?.role}
                </span>
              </div>

              <CaretDown size={12} className="text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="text-sm">{user?.name}</div>
              <div className="text-xs text-muted-foreground">{user?.email}</div>
            </DropdownMenuLabel>

            <DropdownMenuSeparator />

            <DropdownMenuItem onClick={() => (window.location.href = "/profile")}>
              <User size={14} className="mr-2" />
              Profile
            </DropdownMenuItem>

            <DropdownMenuItem onClick={() => (window.location.href = "/settings")}>
              <Gear size={14} className="mr-2" />
              Preferences
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            <DropdownMenuItem onClick={logout} data-testid="logout-btn">
              <SignOut size={14} className="mr-2" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}